import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { RACQAddressResult, ISuburbLookupResult, AddressLookupConfig, Address } from '@/interfaces';
import { AddressLookupService } from '@/services/store';
import { AUSTRALIA, AUSTRALIA_ISO, QLD } from '@/constants/general-words.const';
import { isEmptyObject } from '@/utils/check-helpers';
import { isPOBoxAddress, isPostalAddress } from '@/utils/regex-helpers';
import { buildInternationalAddressLine as buildInternationalAddress, joinAddressLine } from '@/utils/common-helper';

export const emitManualAddressSelected = 'manualAddressSelected';
export const emitAddressSelected = 'addressSelected';
export const emitAddressReset = 'addressReset';
export const addressLookupEmits = [emitManualAddressSelected, emitAddressSelected, emitAddressReset];

const _useAddressLookupOptionsStore = () => {
  const config = ref<AddressLookupConfig>();

  //Used to block certain things from happening whilst the component is initially loading.
  //E.g Preventing resetManualAddress from firing when setting isOverseasSelection.
  const componentLoading = ref<boolean>(false);

  //Component selections
  const isOverseasSelection = ref<boolean>(false);
  const postalSameAsInsuredAddressSelection = ref<boolean>(false);
  const isCycloneCovered = ref<boolean>();
  const externalValidationMessage = ref<string>();
  const manualEditEnabled = ref<boolean>(false);
  const insuredAddress = ref<Partial<RACQAddressResult>>();
  const postalAddress = ref<Partial<RACQAddressResult>>();
  const useAddress = ref<Partial<RACQAddressResult>>();
  const isManualEntries = ref<boolean>(false);
  const showInvalidInsuranceRiskSuburbMessage = ref<boolean>();
  const showRiskAddressNotFullyVerifiedMessage = ref<boolean>();
  const showRiskAddressCycloneCheckFailedMessage = ref<boolean>();
  const riskAddressNotFullyVerifiedIsError = ref<boolean>();
  const userHasTriggeredChanged = ref<boolean>(false);
  const showDividerForManualAddress = ref(false);

  const autoAddressComponents = ref<Partial<RACQAddressResult>>({});
  const manualAddressComponents = ref<Partial<RACQAddressResult>>({});

  const suburbLookupOptions = ref<ISuburbLookupResult[]>([]);

  const activeAddressComponents = computed(() => {
    if (isManualEntries.value) {
      return manualAddressComponents.value;
    } else {
      return autoAddressComponents.value;
    }
  });

  /**
   * Setup address component for logged user prefilling IQ forms
   * @param configOptions
   * @param residentialAddress
   */
  const setupAddressFromResidential = (configOptions: AddressLookupConfig, residentialAddress?: Address) => {
    if (!residentialAddress?.dpid) return; // do not prefill it address is not verified
    config.value = configOptions;
    setConfigDefaults();

    autoAddressComponents.value =
      insuredAddress.value =
      useAddress.value =
        {
          addressLine: residentialAddress?.formattedStreet,
          buildingLevel: residentialAddress?.buildingLevel,
          unitNumber: residentialAddress?.unit,
          streetNumber: residentialAddress?.streetNumber,
          street: residentialAddress?.street,
          suburb: residentialAddress?.suburb,
          postcode: residentialAddress?.postcode,
          state: residentialAddress?.state,
          country: residentialAddress?.country,
          dpid: residentialAddress?.dpid,
        };
    return autoAddressComponents.value;
  };

  /**
   * Setup for retrieve quote
   *
   * @param configOptions
   * @param quoteInsuredAddress
   * @param quotePostalAddress
   */
  const setupAddressComponent = (
    configOptions: AddressLookupConfig,
    quoteInsuredAddress?: RACQAddressResult,
    quotePostalAddress?: RACQAddressResult
  ) => {
    componentLoading.value = true;
    config.value = configOptions;
    setConfigDefaults();

    insuredAddress.value = quoteInsuredAddress;
    postalAddress.value = quotePostalAddress;

    if (quotePostalAddress) {
      useAddress.value = quotePostalAddress;
      postalSameAsInsuredAddressSelection.value = checkPostalIsSameAsInsured(quotePostalAddress);
    } else {
      useAddress.value = quoteInsuredAddress;
    }
  };

  // Set config item(s) not explicitly set to simplify logic to simple boolean checks.
  const setConfigDefaults = () => {
    if (!config.value) return;
    config.value.allowOverseasAddress = config.value.allowOverseasAddress ?? false;
    config.value.allowPhysicalAddressesOnly = config.value.allowPhysicalAddressesOnly ?? false;
    config.value.isCycloneCheckRequired = config.value.isCycloneCheckRequired ?? false;
  };

  const onSameAsPostalAddressChanged = () => {
    //toggle value, seems to be issue with timing of model and value
    postalSameAsInsuredAddressSelection.value = !postalSameAsInsuredAddressSelection.value;

    //Clear postal address if unticked
    if (!postalSameAsInsuredAddressSelection.value) {
      postalAddress.value = {};
    }

    useAddress.value = postalSameAsInsuredAddressSelection.value ? insuredAddress.value : postalAddress.value;
    isManualEntries.value = false;

    if (postalSameAsInsuredAddressSelection.value) {
      isOverseasSelection.value = false;
      manualEditEnabled.value = false;
    }

    return useAddress.value;
  };

  const onIsOverseasChanged = (isOverseas: boolean, enableManualEntry: boolean) => {
    isOverseasSelection.value = isOverseas;
    manualEditEnabled.value = enableManualEntry;
    if (!isOverseas) {
      isManualEntries.value = false;
    }

    if (!isOverseas) {
      manualAddressComponents.value.country = AUSTRALIA;
    } else {
      postalSameAsInsuredAddressSelection.value = false;
      manualAddressComponents.value.state = '';
    }
  };

  const onEnableManualEntrySelected = () => {
    manualEditEnabled.value = true;
    isManualEntries.value = true;
    isOverseasSelection.value = false;
    postalSameAsInsuredAddressSelection.value = false;
    manualAddressComponents.value.state = QLD;
    clearAllWarningsAndErrors();
  };

  const onAutoAddressSelected = (addressComponents: Partial<RACQAddressResult>) => {
    isManualEntries.value = false;
    manualEditEnabled.value = false;
    postalSameAsInsuredAddressSelection.value = checkPostalIsSameAsInsured(addressComponents);
  };

  const toggleManualFormDisabled = () => {
    manualEditEnabled.value = !manualEditEnabled.value;
  };

  const clearAllWarningsAndErrors = () => {
    showInvalidInsuranceRiskSuburbMessage.value = false;
    showRiskAddressNotFullyVerifiedMessage.value = false;
    showRiskAddressCycloneCheckFailedMessage.value = false;
    riskAddressNotFullyVerifiedIsError.value = false;
    externalValidationMessage.value = '';
  };

  const showTitle = () => {
    return config.value?.allowOverseasAddress && config.value?.isPostalAddress;
  };

  /**
   * Get all suburbs from reference data and match with the current input
   */
  const getSuburbsByPostcode = async () => {
    const address = activeAddressComponents.value;
    if (address.postcode) {
      const results = await AddressLookupService.getSuburbsByPostcode(
        address.postcode,
        config.value?.allowPhysicalAddressesOnly ?? false
      );
      const match = results?.find(
        (option) =>
          option.suburb.toLowerCase() == address.suburb?.toLowerCase() &&
          option.state.toLowerCase() == address.state?.toLowerCase()
      );

      return { match, results };
    }
    return null;
  };

  /**
   * Check to see if the location matches a list of valid risk suburbs that RACQ covers and format it with LURN Code and optionally check
   * for cyclone cover
   */
  const applyRiskAddressBusinessRules = async () => {
    const address = activeAddressComponents.value;
    if (address.postcode) {
      const riskSuburbs = await AddressLookupService.getInsuranceRiskSuburbs(address.postcode);
      const match = riskSuburbs.find(
        (option) =>
          option.suburb.toLowerCase() == address.suburb?.toLowerCase() &&
          option.state.toLowerCase() == address.state?.toLowerCase()
      );

      showInvalidInsuranceRiskSuburbMessage.value = !match;
      riskAddressNotFullyVerifiedIsError.value = false;
      showRiskAddressNotFullyVerifiedMessage.value = false;
      showRiskAddressCycloneCheckFailedMessage.value = false;

      if (match) {
        const formatted = await formatRiskAddress();

        const isCycloneCovered = await checkIsCycloneCovered();

        riskAddressNotFullyVerifiedIsError.value = formatted?.lurnScale === undefined;
        showRiskAddressNotFullyVerifiedMessage.value =
          formatted?.lurnScale === undefined || (isManualEntries.value && formatted.lurnScale > 1); // only show not fully verified if address was manually entered
        showRiskAddressCycloneCheckFailedMessage.value =
          config.value?.isCycloneCheckRequired && isCycloneCovered === undefined;
      }

      return { match, riskSuburbs };
    }
    return null;
  };

  /**
   * Check to see if the location has cyclone insurance coverage
   */
  const checkIsCycloneCovered = async () => {
    const address = activeAddressComponents.value;
    if (config.value?.isCycloneCheckRequired && address.suburb && address.postcode && address.state) {
      try {
        const response = await AddressLookupService.getIsCycloneCovered(
          address.suburb,
          address.postcode,
          address.state
        );
        address.isCycloneCovered = response?.isCovered;
      } catch (error) {
        console.error(error);
      }
    } else {
      address.isCycloneCovered = undefined;
    }
    return address.isCycloneCovered;
  };

  /**
   * Format risk address and obtain lurn + gnaf info
   */
  async function formatRiskAddress() {
    const address = activeAddressComponents.value;
    const addressline =
      address.addressLine ?? [address.unitNumber, address.streetNumber, address.street].filter((o) => o).join(' ');
    try {
      const res = await AddressLookupService.formatRiskAddress({
        addressline,
        unitNumber: address.unitNumber,
        streetNumber: address.streetNumber,
        streetName: address.street,
        postcode: address.postcode,
        suburb: address.suburb,
        state: address.state,
      });
      address.lurnCode = res?.matchedAddress?.lurnKey;
      address.lurnScale = res?.matchedAddress?.lurnScale;
      address.gnafAddressDetailPID = res?.matchedAddress?.gnafAddressDetailPID;
    } catch (error) {
      console.error(error);
    }
    return address;
  }

  //Used to determine if the address has yet to be populated.
  //E.g: When first coming into purchase journey a postal address will not be populated yet and most fields will be undefined.
  const isValidAddress = (value: Partial<RACQAddressResult>) => {
    if (!value) return false;
    if (isPOBoxAddress(value?.streetNumber ?? value?.street ?? '')) {
      return value && (value.suburb?.length ?? 0) > 0 && (value.postcode?.length ?? 0) > 0;
    } else if (
      value.country?.toUpperCase() !== AUSTRALIA.toUpperCase() &&
      value.country?.toUpperCase() !== AUSTRALIA_ISO.toUpperCase()
    ) {
      return (
        value &&
        (value.streetNumber?.length ?? 0) > 0 &&
        (value.street?.length ?? 0) > 0 &&
        (value.suburb?.length ?? 0) > 0
      );
    } else {
      return (
        value &&
        (value.streetNumber?.length ?? 0) > 0 &&
        (value.street?.length ?? 0) > 0 &&
        (value.suburb?.length ?? 0) > 0 &&
        (value.postcode?.length ?? 0) > 0 &&
        (value.state?.length ?? 0) > 0
      );
    }
  };

  /**
   * Checks streetNumber or street just to determine if it has been previously saved.
   * @param value
   * @returns
   */
  const isAddressPreviouslySaved = (value: Partial<RACQAddressResult>) => {
    if (!value) return false;

    if (value?.streetNumber) {
      return value?.streetNumber?.length > 0;
    }

    if (value?.street) {
      return value?.street?.length > 0;
    }

    return false;
  };

  const checkPostalIsSameAsInsured = (postalAddress: Partial<RACQAddressResult>) => {
    return (
      postalAddress?.unitNumber === insuredAddress?.value?.unitNumber &&
      postalAddress?.streetNumber === insuredAddress?.value?.streetNumber &&
      postalAddress?.dpid == insuredAddress?.value?.dpid
    );
  };

  const resetManualAddress = () => {
    isManualEntries.value = false;
    manualAddressComponents.value = {};
    suburbLookupOptions.value = [];
    clearAllWarningsAndErrors();
  };

  const setManualAddress = (address: Partial<RACQAddressResult>) => {
    isManualEntries.value = true;
    manualAddressComponents.value = address;
    manualAddressComponents.value.addressLine = joinAddressLine(address);
    autoAddressComponents.value = {};
    isOverseasSelection.value = isOverseasManualAddress();
    manualEditEnabled.value = false;
  };

  const setAutoAddress = (address: Partial<RACQAddressResult>) => {
    isManualEntries.value = false;
    autoAddressComponents.value = address;
    manualAddressComponents.value = {};
  };

  /**
   * Build full address line used when selecting auto address
   *
   * @param address
   * @returns
   */
  const buildAddressLine = (address: Partial<RACQAddressResult>) => {
    let addressLine = joinAddressLine(address);

    const isPostal = isPostalAddress(addressLine);
    if (isPostal) {
      return [addressLine?.trim() + ',', address.suburb?.trim(), address.state?.trim(), address.postcode?.trim()].join(
        ' '
      );
    }

    if (!isValidAddress(address)) return;

    addressLine += address.suburb?.trim().length ? `, ${address.suburb.trim()} ` : '';

    if (!isOverseasSelection.value) {
      addressLine += address.state?.trim().length ? `${address.state.trim()} ` : '';
    }

    addressLine += address.postcode?.trim().length ? `${address.postcode.trim()}` : '';

    if (isOverseasSelection.value) {
      addressLine += address.postcode?.trim().length ? ' ' : '';
      addressLine += address.country?.trim().length ? `${address.country.trim()}` : '';
    }
    return addressLine;
  };

  /**
   * Handles showing international address in self service for read only mode until other address related fixes are done.
   * https://wiki.racqgroup.local/confluence/spaces/WS/pages/373739427/International+address+issues
   * @param address
   * @param country
   * @returns
   */
  const buildInternationalAddressLine = (address: Partial<RACQAddressResult>, country: string) => {
    return buildInternationalAddress(address, country);
  };

  /*
  Only a manual address should be able to be overseas address.
  */
  const isOverseasManualAddress = () => {
    const isOverseas =
      !isEmptyObject(manualAddressComponents?.value) &&
      manualAddressComponents.value.country !== undefined &&
      manualAddressComponents.value.country !== '' &&
      manualAddressComponents.value.country?.toLowerCase() !== AUSTRALIA.toLowerCase() &&
      manualAddressComponents.value.country?.toLowerCase() !== AUSTRALIA_ISO.toLowerCase();
    return isOverseas;
  };

  const hasPOBoxEntry = () => {
    let isPOBox = isPOBoxAddress(manualAddressComponents?.value?.streetNumber ?? '');
    if (!isPOBox) {
      isPOBox = isPOBoxAddress(manualAddressComponents?.value?.street ?? '');
    }

    if (!isPOBox) {
      isPOBox = isPOBoxAddress(joinAddressLine(manualAddressComponents.value));
    }

    if (isPOBox && config.value?.allowPhysicalAddressesOnly === false) {
      manualAddressComponents.value.unitNumber = '';
      manualAddressComponents.value.streetNumber = '';
    }

    return isPOBox;
  };

  return {
    config,
    isOverseasSelection,
    postalSameAsInsuredAddressSelection,
    isCycloneCovered,
    externalValidationMessage,
    manualEditEnabled,
    postalAddress,
    insuredAddress,
    useAddress,
    isManualEntries,
    autoAddressComponents,
    manualAddressComponents,
    activeAddressComponents,
    suburbLookupOptions,
    showInvalidInsuranceRiskSuburbMessage,
    riskAddressNotFullyVerifiedIsError,
    showRiskAddressNotFullyVerifiedMessage,
    showRiskAddressCycloneCheckFailedMessage,
    componentLoading,
    showDividerForManualAddress,
    setupAddressComponent,
    setupAddressFromResidential,
    onSameAsPostalAddressChanged,
    onIsOverseasChanged,
    onEnableManualEntrySelected,
    onAutoAddressSelected,
    toggleManualFormDisabled,
    showTitle,
    getSuburbsByPostcode,
    applyRiskAddressBusinessRules,
    isValidAddress,
    checkPostalIsSameAsInsured,
    resetManualAddress,
    setManualAddress,
    setAutoAddress,
    joinAddressLine,
    buildAddressLine,
    buildInternationalAddressLine,
    checkIsCycloneCovered,
    isOverseasManualAddress,
    clearAllWarningsAndErrors,
    isAddressPreviouslySaved,
    userHasTriggeredChanged,
    hasPOBoxEntry,
  };
};

const useAddressLookupOptionsStore = (storeIdentifier: string) => {
  const storeName = `addressLookupOptionsStore-${storeIdentifier}`;
  const store = defineStore(storeName, _useAddressLookupOptionsStore);
  return store();
};

const addressLookupOptionsStores = new Map<string, ReturnType<typeof defineAddressStore>>();

function defineAddressStore(instanceId: string) {
  const addressLookupOptionsStore = useAddressLookupOptionsStore(instanceId);
  return addressLookupOptionsStore;
}

export const useMultiInstanceAddressOptionsStore = defineStore('multiInstanceAddressOptionsStore', () => {
  const itemIds = ref<string[]>([]);

  function addInstance(instanceId: string) {
    if (itemIds.value.indexOf(instanceId) > -1) return;

    addressLookupOptionsStores.set(instanceId, useAddressLookupOptionsStore(instanceId));
    itemIds.value.push(instanceId);
  }

  function getInstance(instanceId: string) {
    const storeInstance = addressLookupOptionsStores.get(instanceId);

    if (!storeInstance) {
      throw 'Instance not found';
    }

    return storeInstance;
  }

  function removeItem(instanceId: string) {
    const index = itemIds.value.indexOf(instanceId);
    if (index == -1) return;
    addressLookupOptionsStores.get(itemIds.value[index])!.$dispose();
    addressLookupOptionsStores.delete(itemIds.value[index]);
    itemIds.value.splice(index, 1);
  }

  return {
    itemIds,
    addInstance,
    getInstance,
    removeItem,
  };
});
